import { useEffect, useState } from "react";
import { AlertTriangle } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/lib/supabase";
import { readChannelHealth, type ChannelHealthStatus } from "@/lib/channel-health";

interface ExpiredChannel {
  id: string;
  name: string;
  channel_type: string;
  status: ChannelHealthStatus;
}

export function ChannelTokenExpiredBanner() {
  const { activeWorkspaceId } = useAuth();
  const navigate = useNavigate();
  const [expired, setExpired] = useState<ExpiredChannel[]>([]);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    if (!activeWorkspaceId) {
      setExpired([]);
      return;
    }
    let mounted = true;
    const load = async () => {
      const { data } = await supabase
        .from("channels")
        .select("id, name, channel_type, config, is_active")
        .eq("organization_id", activeWorkspaceId)
        .eq("is_active", true)
        .in("channel_type", ["facebook", "instagram", "whatsapp"]);
      if (!mounted) return;
      // Derivado con readChannelHealth y no a mano: repetir las banderas acá es
      // exactamente cómo el banner se quedó atrás de los estados nuevos.
      const bad = (data || [])
        .map((c: any) => ({
          id: c.id,
          name: c.name,
          channel_type: c.channel_type,
          status: readChannelHealth(c.channel_type, c.config as Record<string, unknown>).status,
        }))
        .filter((c) => c.status !== "connected" && c.status !== "missing_credentials");
      setExpired(bad);
    };
    load();
    const interval = setInterval(load, 60_000); // refrescar cada minuto
    return () => { mounted = false; clearInterval(interval); };
  }, [activeWorkspaceId]);

  if (dismissed || expired.length === 0) return null;

  const labelFor = (t: string) =>
    t === "facebook" ? "Facebook" : t === "instagram" ? "Instagram" : "WhatsApp";

  const list = expired
    .map((c) => `${labelFor(c.channel_type)} (${c.name})`)
    .slice(0, 3)
    .join(", ");
  const extra = expired.length > 3 ? ` y ${expired.length - 3} más` : "";

  // Cuando lo que se cayó es la entrega de Meta, el aviso no es "la IA no
  // contesta": es que los mensajes ni siquiera llegan, y no se recuperan después.
  const inboundDown = expired.some(
    (c) => c.status === "disconnected_from_meta" || c.status === "webhook_unsubscribed",
  );

  return (
    <div className="shadow-panel mx-3 mt-3 rounded-xl bg-destructive px-4 py-3 text-destructive-foreground">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-start gap-3 flex-1 min-w-0">
          <AlertTriangle className="h-5 w-5 flex-shrink-0 mt-0.5" />
          <div className="text-sm">
            <strong>{inboundDown ? "Canal sin recibir mensajes:" : "Canal sin poder responder:"}</strong>{" "}
            <span>{list}{extra}</span>.{" "}
            {inboundDown
              ? "Lo que te escriban por ahí no está llegando a Flow y no queda en cola: se pierde."
              : "Tu IA no podrá contestar a esos clientes hasta que reconectes la cuenta."}
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => navigate("/app/settings/channels")}
            className="bg-background text-foreground hover:bg-background/90 px-4 py-1.5 rounded-md text-sm font-semibold whitespace-nowrap"
          >
            Reconectar ahora
          </button>
          <button
            onClick={() => setDismissed(true)}
            className="text-destructive-foreground/80 hover:text-destructive-foreground text-xs px-2"
            aria-label="Cerrar"
          >
            ✕
          </button>
        </div>
      </div>
    </div>
  );
}
